'use client'

import { useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { isMarketingPath } from '@/components/behmn/routes'

const STORAGE_KEY = 'hmn-cookie-consent'

export function CookieConsent() {
  const [visible, setVisible] = useState(false)
  // On the behmn marketing site the banner takes the site's design (see
  // components/behmn/platform.css); everywhere else it keeps the app's.
  const marketing = isMarketingPath(usePathname())
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY)
    if (consent) return
    // The marketing site has already laid the page out around the bar (SiteFrame
    // sets html.behmn-consent-open before first paint), so the bar shows at once.
    if (marketing) {
      setVisible(true)
      return
    }
    // Small delay so the banner doesn't flash on page load
    const timer = setTimeout(() => setVisible(true), 1500)
    return () => clearTimeout(timer)
  }, [marketing])

  // While it is up on the marketing site the bar is docked to the bottom edge
  // and the page is sized around it (components/behmn/platform.css and
  // first-screen.css). Publish its real height for that, and hand the strip
  // back once a choice is made.
  useEffect(() => {
    const el = ref.current
    if (!visible || !marketing || !el) return
    const root = document.documentElement
    const publish = () => root.style.setProperty('--consent-h', `${Math.ceil(el.getBoundingClientRect().height)}px`)
    publish()
    root.classList.add('behmn-consent-open')
    const observer = new ResizeObserver(publish)
    observer.observe(el)
    return () => {
      observer.disconnect()
      root.classList.remove('behmn-consent-open')
      root.style.removeProperty('--consent-h')
    }
  }, [visible, marketing])

  function accept() {
    localStorage.setItem(STORAGE_KEY, 'accepted')
    setVisible(false)
    // Reload to initialize advertising pixels
    window.location.reload()
  }

  function decline() {
    localStorage.setItem(STORAGE_KEY, 'declined')
    setVisible(false)
  }

  if (!visible) return null

  if (marketing) {
    return (
      <div ref={ref} data-cookie-consent className="behmn-consent" role="dialog" aria-label="Cookie consent">
        <div className="behmn-consent__inner">
          <p className="behmn-consent__text">We use cookies to improve your experience and measure site performance.</p>
          <div className="behmn-consent__actions">
            <button type="button" onClick={decline} className="behmn-consent__btn behmn-consent__btn--ghost">
              Decline
            </button>
            <button type="button" onClick={accept} className="behmn-consent__btn behmn-consent__btn--primary">
              Accept
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      data-cookie-consent
      className="fixed bottom-0 left-0 right-0 z-[9999] bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 px-4 py-3 sm:px-6 sm:py-4"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="mx-auto max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-sm text-gray-300 text-center sm:text-left">
          We use cookies to improve your experience and measure site performance.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={decline}
            className="px-4 py-1.5 text-sm text-gray-400 hover:text-white transition-colors rounded-md border border-gray-600 hover:border-gray-500"
          >
            Decline
          </button>
          <button
            onClick={accept}
            className="px-4 py-1.5 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-500 transition-colors rounded-md"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  )
}
